/**
 * ATL-1371 — one place where "what the merchant typed" becomes "what the books hold".
 *
 * WHY THIS EXISTS. Monetary fields across the app each made their own two decisions — which keypad
 * to render, and how to turn its text into a number — and both decisions were wrong in ways that do
 * not announce themselves:
 *
 *   • `keyboardType='number-pad'` is a DIGITS-ONLY keypad. iOS maps it to `UIKeyboardTypeNumberPad`
 *     and Android to a bare `InputType.TYPE_CLASS_NUMBER`; neither renders a decimal separator key.
 *     A merchant on iPhone 16 Pro Max / iOS 26.0.1 could not enter 36.5 into the Money Transaction
 *     amount at all. `components/MoneyTextInput.js` is the fix for that half.
 *
 *   • The decimal key a keypad DOES render is the device locale's separator, so a ru/kk device emits
 *     a COMMA. `parseFloat('36,5')` is 36 and `parseInt('36.5')` is 36 — neither is NaN, so no guard
 *     fires and no error is shown. The wrong amount is simply saved. That is the dangerous half:
 *     fixing only the keyboard converts "cannot enter 36.5" into "enters 36.5, records 36".
 *
 * So: normalise at the keystroke, parse STRICTLY at the save. Strictness is deliberate. `parseFloat`
 * returns a number for '12abc', '1.2.3' and '1e999'; every one of those would be a believable amount
 * in a ledger nobody can reconcile. An unreadable amount must refuse the save, not round itself off.
 */

/** Matches a plain decimal only — no exponent, no thousands separators, no trailing junk.
 *  A lone separator or sign ('.', ',', '-') fails, as does a second separator ('1.2.3'). */
const PLAIN_DECIMAL = /^-?(?:\d+(?:\.\d*)?|\.\d+)$/

/**
 * Keystroke-level normalisation for a monetary field: the locale comma becomes a dot.
 *
 * EVERY comma is converted, not just the first. Converting only the first would leave '1,2,3' as
 * '1.2,3', and dropping the extras would turn it into a plausible 1.23 — both hide a typo that the
 * strict parse below is meant to surface. The value stays visibly unparseable instead.
 *
 * Nothing else is stripped. Letters and spaces a paste may carry are left in place so the merchant
 * sees exactly what the field holds, and {@link parseDecimalAmount} refuses them at save time.
 *
 * @param {unknown} text
 * @returns {string} always a string, so a caller can store it in a TextInput's value without a guard
 */
export const normalizeDecimalSeparator = (text) => {
  if (text === null || text === undefined) return ''
  return String(text).replace(/,/g, '.')
}

/**
 * Save-level parse of a monetary field: a finite number, or `null` for anything unreadable.
 *
 * `null` — never NaN and never a fallback number — is the whole contract: callers branch on it and
 * show the field's existing "not a number" error rather than writing a guess. A negative is accepted
 * so a stored negative row still round-trips through an edit box; no numeric keypad can produce a
 * minus, so this is about data already in the books, not about new input.
 *
 * @param {unknown} raw text from a monetary input, or a number already
 * @returns {number|null}
 */
/**
 * Add two amounts without handing the merchant IEEE-754's rounding noise.
 *
 * `0.14 + 1` is `1.1400000000000001` in JavaScript, and a quick-amount button that writes its
 * result back into a visible input renders exactly that. This never used to surface because the
 * one caller coerced with `parseInt` first — losing the fraction, which is the defect this module
 * exists to fix — so the tail arrived WITH the fix and has to be handled with it.
 *
 * Six decimal places: the widest real currency precision is four (CLF), so nothing a merchant can
 * legitimately enter is touched, while the ~1e-16 relative error of a binary double is well inside
 * the rounding for any amount a cash drawer holds.
 *
 * `null` — the same contract as {@link parseDecimalAmount} — for anything that is not a sum of two
 * finite numbers. Callers must handle it; the one caller that renders the result leaves its box
 * unchanged rather than dereferencing null.
 *
 * @param {number} a
 * @param {number} b
 * @returns {number|null}
 */
export const addDecimalAmounts = (a, b) => {
  // Numbers only. `'5' + 1` is the string `'51'`, and `Number.isFinite('51')` is false, so without
  // this the guard below would hand a caller a believable wrong value — in the one module whose
  // whole thesis is that a money helper returns null rather than a plausible number.
  if (typeof a !== 'number' || typeof b !== 'number') return null
  const sum = a + b
  if (!Number.isFinite(sum)) return null
  const rounded = Number(sum.toFixed(6))
  // `-0`, which a rounded-away negative residual produces, is normalised to `0`. It compares equal
  // to `0` and even prints as "0", so nothing downstream misbehaves — but it is a distinct value
  // to `Object.is`, which means a test or a memo comparator can tell the two apart when no part of
  // the product can. Canonical output, one less thing that is true only sometimes.
  return rounded === 0 ? 0 : rounded
}

export const parseDecimalAmount = (raw) => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
  if (typeof raw !== 'string') return null

  const text = normalizeDecimalSeparator(raw).trim()
  if (!PLAIN_DECIMAL.test(text)) return null

  const parsed = Number(text)
  // `Number('1.')` is 1 and `Number('.5')` is 0.5 — both are mid-typing values the pattern allows.
  return Number.isFinite(parsed) ? parsed : null
}
